import { useCallback, useEffect, useState } from 'react';

/**
 * Update-Behandlung für den Service Worker.
 *
 * Hintergrund (Befund B-02, 15.09.2026): `registerType: 'prompt'` ist in
 * vite.config.ts gesetzt und in ADR-021 begründet — „kein stilles Auto-Update
 * mitten in der Erfassung". Die Gegenleistung dieser Entscheidung, nämlich ein
 * Weg, das Update anzunehmen, fehlte. Folge: Der neue Service Worker
 * installierte sich und wartete; die alte App bediente weiter aus dem Cache,
 * bis alle Tabs geschlossen sind — auf einem Handy unter Umständen nie.
 *
 * Der erzeugte Service Worker kann `SKIP_WAITING` bereits
 * (`"SKIP_WAITING" === e.data.type && self.skipWaiting()`). Es fehlte allein
 * die Seite, die die Nachricht schickt. Deshalb kommt der Fix ohne Eingriff in
 * die SW-Erzeugung aus und ohne zusätzliche Abhängigkeit.
 *
 * GRENZE: Das hilft nur Clients, die BEREITS eine Fassung mit dieser
 * Behandlung ausführen. Wer noch eine ältere App hält, wird davon nicht
 * eingeholt — dort entscheidet der bereits installierte Service Worker, und
 * der kennt niemanden, der ihm etwas schicken könnte. Diese Geräte brauchen
 * einmalig einen Handgriff (alle Tabs schließen oder Website-Daten löschen).
 * Siehe ADR-033.
 */
export function usePwaUpdate(): { updateVerfuegbar: boolean; anwenden: () => void } {
  const [wartend, setWartend] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    let abgemeldet = false;

    void navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg || abgemeldet) return;

      // Schon installiert und wartend (z. B. Deploy während die Seite offen war).
      if (reg.waiting) setWartend(reg.waiting);

      reg.addEventListener('updatefound', () => {
        const neuer = reg.installing;
        if (!neuer) return;
        neuer.addEventListener('statechange', () => {
          // controller !== null: es gibt eine Vorgängerversion, also ist das
          // ein Update und keine Erstinstallation.
          if (neuer.state === 'installed' && navigator.serviceWorker.controller) {
            setWartend(neuer);
          }
        });
      });

      // Einmal aktiv nachfragen: ein Tab, der lange offen liegt, prüft sonst
      // erst bei der nächsten Navigation.
      void reg.update().catch(() => undefined);
    });

    // Genau EIN Neuladen nach dem Wechsel - sonst kann es sich aufschaukeln.
    let neugeladen = false;
    const beiWechsel = () => {
      if (neugeladen) return;
      neugeladen = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', beiWechsel);

    return () => {
      abgemeldet = true;
      navigator.serviceWorker.removeEventListener('controllerchange', beiWechsel);
    };
  }, []);

  const anwenden = useCallback(() => {
    wartend?.postMessage({ type: 'SKIP_WAITING' });
  }, [wartend]);

  return { updateVerfuegbar: wartend !== null, anwenden };
}
